/**
 * A table's style: a way to draw a table, kept in the library under a name.
 *
 * A table names its style by id and keeps only what it does differently, which
 * is its `look`. What is drawn is the style's settings with the table's own on
 * top, the most specific winning, and the document's settings under both
 * wherever neither says anything (see `look.ts`). Changing a style therefore
 * changes every table that follows it, except in what a table has set itself.
 *
 * Pure. A table and a library in, the look in force and what is adjusted out.
 */

import { isJsonObject, type JsonObject, type JsonValue } from '@/domain/json'
import type { TableSection } from '@/domain/section'
import { readTableLook, withLook, type TableLook } from '@/sections/table/look'

/** A style as the library holds it. */
export interface TableStyle {
  readonly id: number
  readonly name: string
  readonly owner: string
  readonly look: TableLook
  readonly isDefault: boolean
  /** How many tables, in every report, follow it. */
  readonly usage: number
  readonly updatedAt: string
}

/** The id a table gives to say it follows no style at all. */
export const NO_STYLE = 0

/**
 * The style a table follows: the one it names, else the library's default.
 *
 * A table that names one the library no longer has follows nothing, rather than
 * the default: a missing style is worth knowing about, and a default that
 * appeared in its place would hide it.
 */
export function styleOf(
  section: Pick<TableSection, 'styleId'>,
  library: readonly TableStyle[],
): TableStyle | undefined {
  if (section.styleId === NO_STYLE) return undefined
  if (section.styleId === undefined) return library.find((style) => style.isDefault)
  return library.find((style) => style.id === section.styleId)
}

/** The look in force: the style's settings, with the table's own on top. */
export function effectiveLook(
  section: Pick<TableSection, 'styleId' | 'look'>,
  library: readonly TableStyle[],
): TableLook {
  return { ...styleOf(section, library)?.look, ...section.look }
}

/**
 * The settings a table sets differently from its style.
 *
 * A setting a table repeats exactly as the style has it is not an adjustment:
 * nothing would change were it taken off, and flagging it would make somebody
 * hunt for a difference that is not there.
 */
export function adjustedKeys(
  section: Pick<TableSection, 'styleId' | 'look'>,
  library: readonly TableStyle[],
): (keyof TableLook)[] {
  const style = styleOf(section, library)
  if (!style) return []
  const own = section.look ?? {}
  return (Object.keys(own) as (keyof TableLook)[]).filter((key) => own[key] !== style.look[key])
}

/**
 * A table's own look with one setting changed, against the style it follows.
 *
 * Set to what the style already says, the setting leaves the table's own look
 * altogether, so a table goes back to following the style by being set back to it.
 */
export function adjusted<K extends keyof TableLook>(
  section: Pick<TableSection, 'styleId' | 'look'>,
  library: readonly TableStyle[],
  key: K,
  value: TableLook[K] | undefined,
): TableLook {
  const own = section.look ?? {}
  const style = styleOf(section, library)
  return withLook(own, key, style && style.look[key] === value ? undefined : value)
}

/** A style as the server sent it, read setting by setting. */
export function readTableStyle(stored: {
  id: number
  name: string
  owner: string
  style: JsonObject
  is_default: boolean
  usage: number
  updated_at: string
}): TableStyle {
  return {
    id: stored.id,
    name: stored.name,
    owner: stored.owner,
    look: readTableLook({ look: stored.style }),
    isDefault: stored.is_default,
    usage: stored.usage,
    updatedAt: stored.updated_at,
  }
}

/**
 * A written document with each table's look made the one in force.
 *
 * For the exports that rebuild a document from its structure (Word, Excel): they
 * read a table's `look` and know nothing of the library, so what they are given
 * is the style's settings with the table's own on top, and no style id. What is
 * drawn is the same as on the page, which is the point of having styles.
 */
export function withStylesApplied(document: JsonValue, library: readonly TableStyle[]): JsonValue {
  if (Array.isArray(document)) return document.map((one) => withStylesApplied(one, library))
  if (!isJsonObject(document)) return document

  const walked = Object.fromEntries(
    Object.entries(document).map(([key, value]) => [key, withStylesApplied(value, library)]),
  )
  if (walked['kind'] !== 'table') return walked

  const { styleId, look: _own, ...rest } = walked
  const id = typeof styleId === 'number' ? styleId : undefined
  const look = effectiveLook(
    { ...(id === undefined ? {} : { styleId: id }), look: readTableLook(walked) },
    library,
  )
  return Object.keys(look).length > 0 ? { ...rest, look: { ...look } } : rest
}
